import store from '@/store';
import { canGroupSelectedItems, canUnGroupSelectedItems } from '@/utils/layers/groupUtils';
import type { CanvasEx } from '@/typings/canvas';
import type { IEvent, Object as FabricObject } from 'fabric/fabric-impl';

export const CONTEXT_MENU_ID = 'fabric-context-menu';

export const enum CONTEXT_MENU_EVENT {
    SHOW = 'contextmenu:show',
    HIDE = 'contextmenu:hide'
}

export const enum CONTEXT_MENU_ACTION {
    RESIZE_CANVAS = 'resize',
    PRODUCT_SPECS = 'productSpecs',
    DOWNLOAD_IMAGE = 'downloadCanvasImage',
    FIT_TO_CANVAS = 'fitToCanvas',
    BRING_FORWARD = 'bringForward',
    SEND_BACKWARD = 'sendBackward',
    FLIP_HORIZONTAL = 'flipHorizontal',
    FLIP_VERTICAL = 'flipVertical'
}

export const enum FABRIC_MOUSE_CLICK {
    LEFT = 1,
    MIDDLE = 2,
    RIGHT = 3
}

export interface ContextMenuItem {
    label?: string;
    icon?: [string, string] | string;
    hint?: string;
    hidden?: boolean;
    onClick?: () => void;
    action?: CONTEXT_MENU_ACTION;
    isDivider?: boolean;
}

export interface ContextMenu {
    x: number;
    y: number;
    selectedElement?: FabricObject;
    items: ContextMenuItem[];
}

export class FabricContextMenu {
    constructor(public canvas: CanvasEx) {
        this.renderMenu = this.renderMenu.bind(this);
        this.hideMenu = this.hideMenu.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
    }

    private getMenuItems(element?: FabricObject): ContextMenuItem[] {
        if (!element) {
            return [
                {
                    label: 'Resize File',
                    icon: ['fas', 'arrow-up-right-and-arrow-down-left-from-center'],
                    action: CONTEXT_MENU_ACTION.RESIZE_CANVAS
                },
                {
                    label: 'Select Product Specs',
                    icon: ['far', 'tag'],
                    action: CONTEXT_MENU_ACTION.PRODUCT_SPECS,
                    hidden: !window.location.search.includes('forDesignTemplateCreation')
                },
                {
                    label: 'Paste',
                    icon: ['far', 'paste'],
                    hint: 'Ctrl+V',
                    onClick: () => this.canvas.pasteCopiedObjects()
                },
                {
                    label: 'Download Image',
                    icon: ['far', 'down-to-bracket'],
                    action: CONTEXT_MENU_ACTION.DOWNLOAD_IMAGE
                }
            ];
        }

        return [
            {
                label: 'Copy',
                hint: 'Ctrl+C',
                icon: ['far', 'copy'],
                onClick: () => this.canvas.copySelection()
            },
            {
                label: 'Paste',
                icon: ['far', 'paste'],
                hint: 'Ctrl+V',
                onClick: () => this.canvas.pasteCopiedObjects()
            },
            {
                label: 'Duplicate',
                hint: 'Ctrl+D',
                icon: ['far', 'clone-plus'],
                onClick: () => this.canvas._cloneSelection()
            },
            {
                label: 'Delete',
                hint: 'Del',
                icon: ['far', 'trash-can'],
                onClick: () => this.canvas.deleteSelection()
            },
            {
                label: 'Hide',
                icon: ['far', 'eye-slash'],
                onClick: () => store.dispatch('storeLayers/toggleAllVisible')
            },
            {
                label: 'Lock',
                icon: ['far', 'lock'],
                onClick: () => store.dispatch('storeLayers/toggleAllLock')
            },
            {
                label: 'Fit to Canvas',
                hint: 'Shift+F',
                icon: ['far', 'expand'],
                action: CONTEXT_MENU_ACTION.FIT_TO_CANVAS
            },
            {
                label: 'Create Layout',
                hint: 'Ctrl+G',
                icon: ['fak', 'layout'],
                onClick: () => this.canvas.groupSelectedItems(),
                hidden: !canGroupSelectedItems(this.canvas)
            },
            {
                label: 'Remove Layout',
                hint: 'Ctrl+Shift+G',
                icon: ['fak', 'remove-layout'],
                onClick: () => this.canvas.unGroupSelectedItems(),
                hidden: !canUnGroupSelectedItems(this.canvas)
            },
            { isDivider: true },
            {
                label: 'Bring Forward',
                icon: ['far', 'bring-forward'],
                action: CONTEXT_MENU_ACTION.BRING_FORWARD
            },
            {
                label: 'Send Backward',
                icon: ['far', 'send-backward'],
                action: CONTEXT_MENU_ACTION.SEND_BACKWARD
            },
            { isDivider: true },
            {
                label: 'Flip Vertical',
                icon: ['far', 'reflect-vertical'],
                action: CONTEXT_MENU_ACTION.FLIP_VERTICAL
            },
            {
                label: 'Flip Horizontal',
                icon: ['far', 'reflect-horizontal'],
                action: CONTEXT_MENU_ACTION.FLIP_HORIZONTAL
            }
        ];
    }

    private renderMenu(event: IEvent<MouseEvent>) {
        if (event.button != FABRIC_MOUSE_CLICK.RIGHT) return;

        event.e.stopPropagation();
        event.e.preventDefault();

        const element = event.target;
        if (!element) {
            this.canvas.discardActiveObject();
        } else {
            this.canvas.setActiveObject(element);
        }

        const { clientX, clientY } = event.e;

        const contextMenu: ContextMenu = {
            x: clientX,
            // Subtract 30px from the y axis to avoid the offset between the menu and the pointer
            y: clientY - 30,
            items: this.getMenuItems(element),
            selectedElement: element
        };

        this.canvas.fire(CONTEXT_MENU_EVENT.SHOW, contextMenu);

        document.addEventListener('mousedown', this.handleClickOutside);
        if (window.DD_RUM) {
            window.DD_RUM.addAction('Opened Right Click Menu');
        }
    }

    private handleClickOutside(event: MouseEvent) {
        const menu = document.getElementById(CONTEXT_MENU_ID);
        if (menu?.contains(event.target as Node)) return;
        this.hideMenu();
    }

    private hideMenu() {
        this.canvas.fire(CONTEXT_MENU_EVENT.HIDE);
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    init() {
        this.canvas.fireRightClick = true;
        this.canvas.stopContextMenu = true;
        this.canvas.on('mouse:down', this.renderMenu);
    }

    destroy() {
        this.canvas.fireRightClick = false;
        this.canvas.stopContextMenu = false;
        this.canvas.off('mouse:down', this.renderMenu);
    }
}
